import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { OrganizationTypes, Roles } from '@/common/constants';
import Permissions from '@/common/permissions';
import { SnackbarState } from '@/modules/core/core.types';
import { useCurrentOrganization, useHasAccess, useSnackbar } from '@/modules/core/hooks';
import useGeneralInfoStore from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores/useGeneralInfo.store';
import { useOrganizationInfoStore } from '@/modules/employee/Employee/PersonalInformation/OrganizationInfo/stores';
import { useUnlockEmployeeMfaQuery, useUpdateEmployeeRoleQuery } from '@/modules/employee/Employee/queries';
import useSecurityStore from '@/modules/employee/Employee/RegistrationAndLogin/Security/stores/useSecurity.store';
import useUserVerificationStatusStore
  from '@/modules/employee/Employee/RegistrationAndLogin/UserVerificationStatus/stores/useUserVerificationStatus.store';

import LinkedAccounts, { LinkPopupMode } from '../LinkedAccounts/LinkedAccounts';
import useLinkedEmployeesQuery from '../LinkedAccounts/useLinkedEmployees.query';
import ProvideFullAccessConfirmModal from '../UserVerificationStatus/ProvideFullAccessModal/ProvideFullAccessConfirmModal';

import LockConfirmModal from './components/LockConfirmModal';
import RemoveCredentialsConfirmModal from './components/RemoveCredentialsConfirmModal';
import UnlockConfirmModal from './components/UnlockConfirmModal';
import useUserAccountRows from './hooks/useUserAccountRows';
import useLinkedApplicationUsersQuery from './queries/useLinkedApplicationUsers.query';
import useLockApplicationUserQuery from './queries/useLockApplicationUser.query';
import useRemoveApplicationUserQuery from './queries/useRemoveApplicationUser.query';
import useUnlockApplicationUserQuery from './queries/useUnlockApplicationUser.query';
import UserAccountsSecurityComponent from './components';
import { useUserAccountsLockStore } from './stores';
import { SecurityOverride, SecurityRow } from './UserAccountsSecurity.types';

const PERMISSIONS = [{ permission: Permissions.VIEW_CREDENTIALS }];
const LINK_ACCOUNT_PERMISSIONS = [{ permission: Permissions.LINK_EMPLOYEE_ACCOUNT }];
const REMOVE_CREDENTIALS_SNACKBAR_WIDTH = '320px';
const REMOVE_CREDENTIALS_SNACKBAR_HEIGHT = '56px';

interface Props {
  isEditMode?: boolean;
}

const UserAccountsSecurityContainer: React.FC<Props> = ({ isEditMode }) => {
  const hasAccess = useHasAccess(PERMISSIONS);
  const hasLinkAccountAccess = useHasAccess(LINK_ACCOUNT_PERMISSIONS);
  const { params: { id } } = useRouteMatch<{ id: string }>();
  const [linkPopupMode, setLinkPopupMode] = useState<LinkPopupMode | undefined>(undefined);
  const [provideFullAccessRow, setProvideFullAccessRow] = useState<SecurityRow | undefined>(undefined);
  const [lockRow, setLockRow] = useState<SecurityRow | undefined>(undefined);
  const [unlockRow, setUnlockRow] = useState<SecurityRow | undefined>(undefined);
  const [removeRow, setRemoveRow] = useState<SecurityRow | undefined>(undefined);
  const { observingOrganization: { type } } = useCurrentOrganization();

  const currentEmployeeSecurity = useSecurityStore((state) => state.state);
  const currentEmployeeRole = useUserVerificationStatusStore((state) => state.state.roleType);
  const organizationName = useOrganizationInfoStore((state) => state.state.organizationName);
  const { firstName, lastName } = useGeneralInfoStore((state) => state.state);

  const setSecurityState = useSecurityStore((state) => state.setState);
  const setUserVerificationStatusState = useUserVerificationStatusStore((state) => state.setState);

  const [overridesByKey, setOverridesByKey] = useState<Record<string, SecurityOverride>>({});

  const {
    formattedData: { linkId, linkedEmployees, primaryEmployeeId },
    isLoading: isLinkedEmployeesLoading,
  } = useLinkedEmployeesQuery(hasAccess ? id : undefined);

  const {
    formattedData: applicationUsers,
    isLoading: isApplicationUsersLoading,
    isSuccess: isApplicationUsersFetched,
  } = useLinkedApplicationUsersQuery(hasAccess ? linkId : undefined);

  const currentEmployee = useMemo(() => ({
    employeeId: id,
    fullName: `${firstName || ''} ${lastName || ''}`.trim(),
    employerName: organizationName,
    isPrimaryLinkedAccount: primaryEmployeeId ? primaryEmployeeId === id : true,
    username: currentEmployeeSecurity.username,
    lastPasswordUpdate: currentEmployeeSecurity.lastPasswordUpdate,
    roleType: currentEmployeeRole,
    isLoginLocked: currentEmployeeSecurity.isLoginLocked,
    hasMfa: Boolean(currentEmployeeSecurity.requiredParticipantMfa && currentEmployeeSecurity.username),
    isMfaLocked: currentEmployeeSecurity.isMfaLocked,
    mfaLockedTo: currentEmployeeSecurity.mfaLockedTo,
  }), [id, firstName, lastName, currentEmployeeSecurity, organizationName, primaryEmployeeId, currentEmployeeRole]);

  const { accounts, securityRows, hasLockedByAdmin, hasLockedByMfa } = useUserAccountRows({
    currentEmployee,
    linkedEmployees,
    applicationUsers,
    isApplicationUsersEmpty: isApplicationUsersFetched && !applicationUsers.length,
    overridesByKey,
  });

  const setUserAccountsLockState = useUserAccountsLockStore((state) => state.setState);

  useEffect(() => {
    setUserAccountsLockState({ hasLockedByAdmin, hasLockedByMfa });
  }, [hasLockedByAdmin, hasLockedByMfa, setUserAccountsLockState]);

  const { lockApplicationUser, isPending: isLocking } = useLockApplicationUserQuery();
  const { unlockApplicationUser, isPending: isUnlocking } = useUnlockApplicationUserQuery();
  const { removeApplicationUser, isPending: isRemoving } = useRemoveApplicationUserQuery();
  const { unlockEmployeeMfa } = useUnlockEmployeeMfaQuery();
  const { updateEmployeeRole, isLoading: isRoleUpdating } = useUpdateEmployeeRoleQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();

  const applyOverride = useCallback((row: SecurityRow, override: SecurityOverride) => {
    setOverridesByKey((prev) => ({ ...prev, [row.key]: { ...prev[row.key], ...override } }));
    if (row.employeeId === id) {
      if (override.isLoginLocked !== undefined) setSecurityState({ isLoginLocked: override.isLoginLocked });
      if (override.isMfaLocked !== undefined) setSecurityState({ isMfaLocked: override.isMfaLocked });
      if (override.roleType !== undefined) setUserVerificationStatusState({ roleType: override.roleType });
    }
  }, [id, setSecurityState, setUserVerificationStatusState]);

  const handleConfirmLock = useCallback(async () => {
    if (!lockRow?.username) return;
    try {
      await lockApplicationUser(lockRow.username);
      applyOverride(lockRow, { isLoginLocked: true });
      handleAddPermanentSnackbar({ text: 'Credentials have been locked.', closeIcon: true, state: SnackbarState.negative });
    } finally {
      setLockRow(undefined);
    }
  }, [lockRow, lockApplicationUser, applyOverride, handleAddPermanentSnackbar]);

  const handleConfirmUnlock = useCallback(async () => {
    if (!unlockRow?.username) return;
    try {
      await unlockApplicationUser(unlockRow.username);
      applyOverride(unlockRow, { isLoginLocked: false });
      handleAddPermanentSnackbar({ text: 'Credentials have been unlocked.', closeIcon: true, state: SnackbarState.positive });
    } finally {
      setUnlockRow(undefined);
    }
  }, [unlockRow, unlockApplicationUser, applyOverride, handleAddPermanentSnackbar]);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeRow?.username) return;
    try {
      await removeApplicationUser(removeRow.username);
      applyOverride(removeRow, { isRemoved: true, roleType: Roles.noAccess });
      handleAddPermanentSnackbar({
        text: 'User credentials removed. The user must register again to log in.',
        closeIcon: true,
        state: SnackbarState.negative,
        width: REMOVE_CREDENTIALS_SNACKBAR_WIDTH,
        height: REMOVE_CREDENTIALS_SNACKBAR_HEIGHT,
      });
    } finally {
      setRemoveRow(undefined);
    }
  }, [removeRow, removeApplicationUser, applyOverride, handleAddPermanentSnackbar]);

  const handleUnlockMfa = useCallback(async (row: SecurityRow) => {
    if (!row.employeeId) return;
    await unlockEmployeeMfa({ employeeId: row.employeeId });
    applyOverride(row, { isMfaLocked: false });
    handleAddPermanentSnackbar({ text: 'MFA has been unlocked.', closeIcon: true, state: SnackbarState.positive });
  }, [unlockEmployeeMfa, applyOverride, handleAddPermanentSnackbar]);

  const handleConfirmProvideFullAccess = useCallback(async () => {
    if (!provideFullAccessRow?.employeeId) return;
    try {
      await updateEmployeeRole({ employeeId: provideFullAccessRow.employeeId, role: Roles.employee });
      applyOverride(provideFullAccessRow, { roleType: Roles.employee });
      handleAddPermanentSnackbar({ text: 'Full access has been provided.', closeIcon: true, state: SnackbarState.positive });
    } finally {
      setProvideFullAccessRow(undefined);
    }
  }, [provideFullAccessRow, updateEmployeeRole, applyOverride, handleAddPermanentSnackbar]);

  if (!hasAccess) {
    return null;
  }

  const hasLinkedAccountsControls = hasLinkAccountAccess && type !== OrganizationTypes.company;

  return (
    <>
      {provideFullAccessRow ? (
        <ProvideFullAccessConfirmModal
          onSetVisible={() => setProvideFullAccessRow(undefined)}
          onClose={() => setProvideFullAccessRow(undefined)}
          onSubmit={handleConfirmProvideFullAccess}
          isLoading={isRoleUpdating}
        />
      ) : null}
      {lockRow ? (
        <LockConfirmModal
          employeeName={lockRow.employeeId === id ? currentEmployeeSecurity.fullName : undefined}
          isLoading={isLocking}
          onSetVisible={() => setLockRow(undefined)}
          onSubmit={handleConfirmLock}
        />
      ) : null}
      {unlockRow ? (
        <UnlockConfirmModal
          isLoading={isUnlocking}
          onSetVisible={() => setUnlockRow(undefined)}
          onSubmit={handleConfirmUnlock}
        />
      ) : null}
      {removeRow ? (
        <RemoveCredentialsConfirmModal
          isLoading={isRemoving}
          onSetVisible={() => setRemoveRow(undefined)}
          onSubmit={handleConfirmRemove}
        />
      ) : null}
      <UserAccountsSecurityComponent
        isLoading={isLinkedEmployeesLoading || isApplicationUsersLoading}
        accounts={accounts}
        securityRows={securityRows}
        isEditMode={isEditMode}
        hasLinkAccountAccess={hasLinkedAccountsControls}
        onLinkAccount={() => setLinkPopupMode(LinkPopupMode.LINK_ACCOUNT)}
        onShowDetails={() => setLinkPopupMode(LinkPopupMode.PRIMARY_ACCOUNT)}
        onProvideFullAccess={setProvideFullAccessRow}
        onLock={setLockRow}
        onUnlock={setUnlockRow}
        onUnlockMfa={handleUnlockMfa}
        onRemoveCredentials={setRemoveRow}
        linkedAccountsSlot={hasLinkedAccountsControls ? (
        <LinkedAccounts
          isEditMode={isEditMode}
          isSectionHidden
          externalPopupMode={linkPopupMode}
          onPopupClose={() => setLinkPopupMode(undefined)}
        />
      ) : undefined}
      />
    </>
  );
};

export default UserAccountsSecurityContainer;
